<template>
  <div v-if="$store.state.News.errorState.length < 1" class="c-login-error">
    {{ $store.state.News.errorState }}
  </div>
  <div class="p-page-create-edit" v-else-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Drag and drop elements into the main page to create your bespoke form"
    >
      <span v-if="editMode">Edit Form</span>
      <span v-else>Create Form</span>
    </page-title>

    <message v-if="duplicatePage" type="error">
      <h3>There's a problem</h3>
      <p>
        A form with the slug
        <strong>{{ duplicatePage.slug }}</strong> already exists.
      </p>
    </message>
    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="312px"
          v-model="currentForm.current_version.title"
          v-validate="{ required: true }"
          name="title"
          placeholder="Name your form here..."
          :error="errors.first('title')"
          is-inline
        ></form-field>
      </template>

      <template slot="actions">
        <base-button
          v-if="editMode"
          @click="onClickViewForm"
          icon-before="search"
          >View Form</base-button
        >
        <base-button
          v-if="
            editMode &&
              currentVersion.version === currentForm.current_version.version
          "
          primary="true"
          @click="onClickUpdate"
          icon-before="edit"
          >Update</base-button
        >
        <base-button
          v-if="!currentForm.current_version || !editMode"
          @click="onClickDraft"
          >Save Draft</base-button
        >
        <base-button v-if="!editMode" primary="true" @click="onClickPublish"
          >Publish</base-button
        >
        <base-button
          icon-before="back"
          v-if="
            editMode &&
              currentVersion.version !== currentForm.current_version.version
          "
          primary="true"
          @click="onClickReinstate"
          >Re-instate</base-button
        >
      </template>

      <page-builder
        :pageId="articleid"
        @reviewDateUpdated="(newDate) => (articlereview_date = newDate)"
        :activeSbPanel="activeSbPanel"
        type="form"
        :load-pages="false"
        :files-enabled="filesEnabled"
      >
        <template slot="intro">
          <div class="c-pb__no-elements--landscape">
            <div class="c-pb__no-elements-text">
              <p>Ready to add fields to your form?</p>
              <p>
                Select
                <strong>&lsquo;Customise Form&rsquo;</strong> tab in the right
                hand menu
              </p>
            </div>
            <inline-svg name="page-builder-landscape"></inline-svg>
          </div>
        </template>
        <tabs slot="tabs">
          <tabs-item
            :active="activeSbPanel === 'settings'"
            @click="activeSbPanel = 'settings'"
            >Form Settings</tabs-item
          >

          <tabs-item
            :active="activeSbPanel === 'elements'"
            @click="activeSbPanel = 'elements'"
            >Customise Form</tabs-item
          >
        </tabs>

        <div slot="sidebars">
          <div class="c-pb__sb-settings" v-show="activeSbPanel === 'settings'">
            <form-field
              full-width
              label="Form Slug"
              name="form_slug"
              type="text"
              v-model="currentForm.eform_url"
            ></form-field>

            <form-field
              label="Category"
              name="category"
              helper-text="Select the category you want the form to appear in"
              :required="true"
            >
              <form-type-ahead-select
                v-model="currentCategory"
                id="category_id"
                name="category_id"
                :options="orderedCategories"
                async-action="FormCategories/quickList"
                display-field="name"
                @input="onCategoryInput"
                v-if="!loading"
                :disabled="isCategoryDisabled"
                v-validate="{ required: true }"
                data-vv-as="category"
                :error="errors.first('category_id')"
              />
              <p v-else>Loading categories</p>
              <p v-if="isCategoryDisabled">
                <small
                  >Once a form is published, you can not change its
                  category.</small
                >
              </p>
            </form-field>

            <form-field
              label="Responsible Users"
              name="responsible_users"
              required
              helper-text="Give people responsibility of this form"
            >
              <div class="row">
                <div class="col-xs-12">
                  <form-label small required>Primary</form-label>
                  <user-select
                    v-model="currentForm.primary_user"
                    id="form_primary_user"
                    name="form_primary_user"
                    v-validate="{ required: true }"
                    data-vv-as="primary user"
                    :error="errors.first('form_primary_user')"
                    @input="markUserChange"
                  ></user-select>
                </div>
                <div class="col-xs-12">
                  <form-label small required>Secondary</form-label>
                  <user-select
                    v-model="currentForm.secondary_user"
                    id="form_secondary_user"
                    name="form_secondary_user"
                    v-validate="{
                      required: true,
                      is_not: currentForm.primary_user_id,
                    }"
                    data-vv-as="secondary user"
                    :error="errors.first('form_secondary_user')"
                    @input="markUserChange"
                  ></user-select>
                </div>
              </div>
            </form-field>

            <!-- Add Tags -->
            <form-field
              label="Add Tags"
              name="document_tags"
              :helper-text="
                'Help users find your form by tagging relevant keywords'
              "
              v-if="!true"
            >
              <form-multiselect
                :show-valid-tick="true"
                v-model="currentForm.tags"
                :options="tags"
                :multiple="true"
                label="name"
                track-by="name"
                :taggable="true"
                @tag="addTag"
              ></form-multiselect>
            </form-field>

            <form-field
              :label="versionLabels.label"
              :name="versionLabels.document_version_number"
              :helper-text="versionLabels.helper"
              v-model="version"
              v-validate="{ required: true, min_value: 1, decimal: 2 }"
              :data-vv-as="versionLabels.vvAs"
              :error="errors.first(versionLabels.name)"
              required
            ></form-field>

            <form-field
              label="Anonymise responses"
              name="anonymous"
              helper-text="Selecting this will prevent users from seeing specific user information in the results"
            >
              <form-toggle v-model="currentForm.anonymous" />
            </form-field>

            <form-field
              label="Allow multiple responses?"
              name="multiple"
              helper-text="Determine if a user can submit multiple entries, or only submit once"
            >
              <form-toggle v-model="currentForm.multiple" />
            </form-field>

            <form-field
              label="Private"
              name="private"
              helper-text="Do you want the form results to be private?"
            >
              <form-toggle v-model="currentForm.private" />
            </form-field>

            <user-group-select
              @onChange="onUserAdd"
              v-if="currentForm.private"
              :value="selectedUsers"
            />
          </div>
        </div>
      </page-builder>
    </section-block>
    <modal
      v-if="downloadWarning"
      show-close-button
      @close="downloadWarning = false"
    >
      <h2 slot="header">Sorry</h2>

      <p>
        Currently, you can only add one file upload field to an eForm.
      </p>
      <p>
        Please update your form, making sure you only have one file upload
        field, and try to save again.
      </p>
    </modal>
  </div>

  <loading v-else></loading>
</template>

<script>
/**
 * TODO: This is based on the page builder for content, but needs altering to
 * fit the needs of the news section.  Ideally the same 'could' be used for events
 */
import PageBuilder from "@/components/PageBuilder";
import FileUpload from "@/components/FileUpload";
import { BannerUpload, FormToggle, UserGroupSelect } from "@/components/forms";
import { mapState, mapGetters } from "vuex";
import FormTypeAheadSelect from "@/components/FormTypeAheadSelect";
import UserSelect from "@/components/UserSelect";

export default {
  name: "admin-page-edit-create",

  components: {
    PageBuilder,
    FileUpload,
    BannerUpload,
    FormTypeAheadSelect,
    UserSelect,
    FormToggle,
    UserGroupSelect,
  },

  data() {
    return {
      editMode: false,
      loading: false,
      duplicatePage: null,
      activeSbPanel: "settings",
      articlestatus: "",
      articleid: 0,
      downloadWarning: false,
      form: {},
      image_uploaded: false,
      docType: {
        plural: "forms",
        single: "form",
      },
      documentFileTypes: ["gif", "png", "jpg", "jpeg"],
      bannerSrc: null,
      show_date: false,
      originalPageElements: {},
      version: "1.0",
      originalLink: "",
      filesEnabled: true,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.elementsHaveChanged && this.editMode) {
      this.$store.dispatch("createDialog", {
        messageText:
          "Any changes you made will be lost if you navigate away from this page, are you sure?",
        confirm: () => {
          this.$store.commit("pageElements/setType", "content");
          next();
        },
      });
    } else {
      this.$store.commit("pageElements/setType", "content");
      next();
    }
  },

  beforeCreate() {},

  async created() {
    this.loading = true;
    // reset
    this.$store.commit("pageElements/setPageElements", []);
    this.$store.commit("pageElements/setType", "form");
    await this.$store.dispatch("FormCategories/list");
    await this.$store.dispatch("tags/list");

    const { id, versionId } = this.$route.params || null;

    if (id) {
      this.loadForm(id, versionId);
    } else {
      this.$store.dispatch("Forms/reset");
      this.loading = false;
    }
  },

  methods: {
    onUserAdd(value) {
      this.selected_users = [];
      this.currentForm.users = [];
      this.currentForm.user_ids = [];
      value.forEach((val) => {
        //if we are a group, we need to extract the individual user ids
        if (val.id.toString().includes("grp")) {
          val = val.meta.data;
          this.currentForm.users = [...this.currentForm.users, ...val];
          this.currentForm.user_ids = [...this.currentForm.user_ids, ...val];
        } else {
          this.currentForm.users.push(val);
          this.currentForm.user_ids.push(val);
        }
        this.selected_users.push(val);
      });
    },
    async loadForm(formId, versionId) {
      await this.$store.dispatch("Forms/read", { formId, versionId });
      this.retrieveFormElements(formId, versionId);
      this.currentForm.category_id = this.currentForm.category
        ? this.currentForm.category.id
        : null;
      this.loading = false;
      this.editMode = true;
      this.originalLink = this.currentForm.eform_url;
      this.currentForm.eform_url = this.currentForm.eform_url
        .split("/")
        .slice(-1);
      this.version = this.currentVersion.version;
    },
    markUserChange(val) {
      // this.currentForm.primary_user_id = val.id
    },
    onCategoryInput(val) {
      this.currentForm.category_id = val.id;
    },
    updateSlug(val) {
      this.currentForm.slug = val;
    },

    onClickReinstate() {
      this.$store.dispatch("createDialog", {
        // TODO: This does the right thing, but do we need to create its own
        // method in the store so we can alter the confirmation etc.?
        confirm: () => {
          this.$store
            .dispatch("Forms/approveForm", {
              formId: this.$route.params.id,
              versionId: this.$route.params.versionId,
            })
            .then((resp) => {
              this.loadForm(
                this.$route.params.id,
                this.$route.params.versionId
              );
            });
        },
      });
    },

    retrieveFormElements(page, version = null) {
      let { elements } = this.currentVersion;

      if (version) {
        // elements = this.$store.state.Forms.versionElements
      }

      if (!elements) {
        elements = [];
      }

      this.$store.commit("pageElements/setPageElements", elements);

      this.originalPageElements = elements ? elements.slice() : [];
    },

    onClickViewForm() {
      window.open(`/forms${this.originalLink}`);
    },

    onClickDraft() {
      this.onClickPublish(true);
    },

    onClickPublish(saveAsDraft = false) {
      // For testing, we just need to post the JSON somewhere
      const vm = this;

      // if we have more then 1 file upload we need to stop
      if (this.fileUploadCount > 1) {
        this.downloadWarning = true;
        return;
      }

      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true;
        } else {
          vm.showValidationMessage = false;
          vm.currentForm.current_version.elements = [
            ...vm.$store.state.pageElements.pageElements,
          ];

          //set ids for primary and secondary users
          vm.currentForm.primary_user_id = vm.currentForm.primary_user.id;
          vm.currentForm.secondary_user_id = vm.currentForm.secondary_user.id;
          vm.currentForm.current_version.version = vm.version;

          // users
          let userIds = [];
          if (vm.currentForm.users) {
            userIds = vm.currentForm.users.map((usr) => {
              if (typeof usr === "number") {
                return usr;
              }
              return usr.id;
            });
          }

          vm.currentForm.user_ids = userIds;

          //only need certain fields
          const {
            id,
            status,
            primary_user,
            secondary_user,
            self,
            user_favourite,
            favourite_count,
            created_user,
            created_time,
            modified_user,
            modified_time,
            users,
            ...payload
          } = vm.currentForm;

          payload.description = "";

          this.$store
            .dispatch("Forms/createAndPublish", { saveAsDraft, payload })
            .then((page) => {
              vm.loading = false;
              this.$store.commit("pageElements/setPageElements", []);
              // If we are a super admin, publish the page
              // if (this.userCanApprove) {
              //   this.$store.dispatch("pages/approve", page.id).then(resp => {
              //     this.$router.push({
              //       name: "AdminContentPageEdit",
              //       params: { pageId: page.id }
              //     });
              //   });
              // } else {
              this.$router.push({
                name: "AdminFormEditPage",
                params: { id: page.document.data.data.eform[0].id },
              });
              // }
            })
            .catch((error) => {
              vm.loading = false;
              if (error.meta && error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page;
              }
            });
        }
      });
    },

    onClickUpdate(saveAsDraft = false) {
      const { currentForm } = this;
      if (this.fileUploadCount > 1) {
        this.downloadWarning = true;
        return;
      }

      this.loading = true;
      const vm = this;
      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true;
          this.loading = false;
        } else {
          if (
            this.elementsHaveChanged &&
            this.version == currentForm.current_version.version
          ) {
            this.loading = false;
            this.$store.dispatch(
              "displayApiError",
              "You need to change the version number to proceed"
            );
            return;
          } else {
            const pageElments = vm.$store.getters["pageElements/forServer"];
            const editedForm = JSON.parse(JSON.stringify(currentForm));
            const versionId = currentForm.current_version.version;

            // Having issues with the original version id carrying over so..f
            editedForm.current_version.orig_version = versionId;

            // add elements to the current page.
            editedForm.current_version.elements = pageElments;

            // interest groups need to be just ids
            if (!editedForm.interestgroup_ids && editedForm.interestgroups) {
              editedForm.interestgroup_ids = editedForm.interestgroups.map(
                (group) => group.id
              );
            }
            // then remove the interest group bit
            delete editedForm.interestgroups;

            if (!editedForm.current_version.title) {
              editedForm.title = "Untitled Form";
            }

            let action = "Forms/resubmit";

            //set ids for primary and secondary users
            editedForm.primary_user_id = editedForm.primary_user.id;
            editedForm.secondary_user_id = editedForm.secondary_user.id;
            editedForm.current_version.version = vm.version;

            // users
            let userIds = [];
            if (vm.currentForm.users) {
              userIds = editedForm.users.map((usr) => {
                if (typeof usr === "number") {
                  return usr;
                }
                return usr.id;
              });
            }

            editedForm.user_ids = userIds;

            //only need certain fields
            const {
              id,
              status,
              primary_user,
              secondary_user,
              self,
              user_favourite,
              favourite_count,
              created_user,
              created_time,
              modified_user,
              modified_time,
              users,
              ...payload
            } = editedForm;

            this.$store
              .dispatch(action, {
                formId: editedForm.id,
                pageData: payload,
                version: vm.version,
                saveAsDraft,
              })
              .then((response) => {
                vm.page = response;
                vm.loading = false;
              })
              .catch((error) => {
                vm.loading = false;
                if (error.meta.duplicate_page) {
                  vm.duplicatePage = error.meta.duplicate_page;
                }
              });
          }
        }
      });
    },
    addTag() {},
  },

  computed: {
    ...mapState("Forms", ["currentForm", "currentVersion"]),
    ...mapGetters("FormCategories", ["orderedCategories"]),
    ...mapState({
      tags: (state) => state.tags.collection,
    }),
    fileUploadCount() {
      const elems = this.$store.getters["pageElements/forServer"];
      const fieldsTotal = elems.filter((elem) =>
        ["media-file-upload", "media-file"].includes(elem.type)
      ).length;

      const rows = elems.filter((elem) => elem.type == "base-row");
      let rowsTotal = 0;

      if (rows.length > 0) {
        // go through each row to see if it has a file upload field
        rows.forEach((row) => {
          const { leftColumn, rightColumn } = row.properties;
          if (
            ["media-file", "media-file-upload"].includes(
              JSON.parse(leftColumn).type
            )
          ) {
            rowsTotal += 1;
          }
          if (
            ["media-file", "media-file-upload"].includes(
              JSON.parse(rightColumn).type
            )
          ) {
            rowsTotal += 1;
          }
        });
      }

      return fieldsTotal + rowsTotal;
    },
    selectedUsers: {
      get() {
        return this.currentForm.users;
      },
      set(val) {
        return val;
      },
    },
    isCategoryDisabled() {
      return this.editMode && this.version != null;
    },
    versionLabels() {
      if (this.editMode) {
        return {
          label: "Version Number",
          name: "document_version_number",
          helper: "Set the version number for this form",
          vvAs: "version number",
        };
      }

      return {
        label: "Initial Version Number",
        name: "document_starting_version",
        helper: "Set the starting version number for the form",
        model: this.currentForm.current_version.version,
        vvAs: "initial version number",
      };
    },
    newVersion: {
      get() {
        return this.currentForm.current_version.version || "1.0";
      },
      set(newVal, oldVal) {
        this.currentForm.new_version = newVal;
      },
    },
    // TODO: This has been copied from the pageElements
    elementsHaveChanged() {
      const serverEls = this.$store.getters["pageElements/forServer"];
      try {
        if (this.originalPageElements.length !== serverEls.length) {
          return true;
        }

        for (let i = this.originalPageElements.length; i--; ) {
          if (this.originalPageElements[i] !== serverEls[i]) {
            return true;
          }
        }
      } catch (err) {
        // console.log(err.message);
      }

      return false;
    },
    elementList() {
      return this.currentForm.elements;
    },
    currentCategory: {
      get() {
        if (!this.currentForm.category_id) {
          return null;
        }
        const current = this.orderedCategories.filter(
          (item) => item.id === this.currentForm.category_id
        );

        return current[0];
      },
      set(newVal, oldVal) {
        this.currentForm.category_id = newVal.id;
        return newVal;
      },
    },
  },
  watch: {
    fileUploadCount: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.filesEnabled = newValue < 1;
      },
    },
  },
};
</script>

<style lang="scss">
.p-page-create-edit {
  .c-section-block__header-main .c-form__group {
    margin: 0;
  }
}

.default-image-holder {
  min-height: 300px;
  margin-top: 20px;
  background: $colour-nhs-pale-grey;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  width: 100%;

  .c-media-image {
    background: transparent;
  }

  .default_image__preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 0 0;
    z-index: 0;
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .default_image__button {
    position: relative;
    z-index: 1;
  }
}

.c-pb__no-elements--landscape {
  position: relative;

  .c-inline-svg,
  .c-pb__no-elements-text {
    position: absolute;
  }

  .c-pb__no-elements-text {
    text-align: left;
    left: 33%;
    z-index: 5;
  }
}
</style>
